/**
 * A generic object pool (tech-design §4, §8: "object pools … `Decimal` churn
 * kept out of the render loop"). Reusing Pixi display objects instead of
 * allocating per frame keeps GC pauses off the render loop.
 *
 * Deliberately Pixi-agnostic — parameterised over a factory/reset — so it
 * unit-tests headlessly and can pool sprites, graphics, particles, anything.
 */
export class ObjectPool<T> {
  private readonly free: T[] = [];
  private readonly factory: () => T;
  private readonly reset: ((item: T) => void) | undefined;
  private readonly maxIdle: number;
  private liveCount = 0;

  /**
   * @param factory  builds a fresh item when the pool is empty
   * @param options.reset    called on release to return an item to a clean state
   * @param options.maxIdle  cap on retained idle items (excess is dropped for GC)
   * @param options.prefill  items to build up front
   */
  constructor(
    factory: () => T,
    options: {
      reset?: (item: T) => void;
      maxIdle?: number;
      prefill?: number;
    } = {},
  ) {
    this.factory = factory;
    this.reset = options.reset;
    this.maxIdle = options.maxIdle ?? Infinity;
    const prefill = options.prefill ?? 0;
    for (let i = 0; i < prefill; i++) {
      this.free.push(factory());
    }
  }

  /** Number of items currently checked out (acquired but not released). */
  get inUse(): number {
    return this.liveCount;
  }

  /** Number of idle items ready for reuse. */
  get idle(): number {
    return this.free.length;
  }

  /** Take an item from the pool, building one if none are free. */
  acquire(): T {
    const item = this.free.pop() ?? this.factory();
    this.liveCount++;
    return item;
  }

  /** Return an item to the pool. Excess beyond `maxIdle` is dropped for GC. */
  release(item: T): void {
    if (this.liveCount > 0) {
      this.liveCount--;
    }
    this.reset?.(item);
    if (this.free.length < this.maxIdle) {
      this.free.push(item);
    }
  }

  /** Drop all idle items (e.g. when a scene is torn down). */
  clear(): void {
    this.free.length = 0;
  }
}
